import { RefObject, ReactNode } from 'react';
import { DateTime } from 'luxon';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { ScheduledEventFormType } from '@act/shared/data-transfer-objects';
import { NoteFieldRef, NoteFieldSaveOverride } from './NoteField';
import { MANUAL_SCHEDULED_EVENT_NOTE_DRAFT_ID } from './scheduledEventNoteDraft.util';

export const SCHEDULED_EVENT_DATE_FORMAT = 'yyyy-MM-dd';

export const SCHEDULED_EVENT_ANYTIME = -1;

/**
 * Normalizes note text to HTML for form defaultValues (Zod transform runs on submit only).
 * @param note - Plain or HTML note text.
 * @returns HTML suitable for the rich text editor.
 */
export function normalizeScheduledEventNoteForDefault(
  note: string | undefined | null
): string {
  if (!note || note.trim() === '') {
    return '<p><br></p>';
  }
  if (/<[^>]+>/.test(note)) {
    return note;
  }
  return `<p>${note}</p>`;
}

/**
 * Converts a UTC epoch from LLM suggestions into local time-of-day millis.
 * @param epochMs - UTC epoch milliseconds.
 * @returns Local millis for the same clock time, when defined.
 */
export function epochCentralToLocalScheduledEventTime(
  epochMs: number | null | undefined
): number | undefined {
  if (typeof epochMs !== 'number') {
    return undefined;
  }
  const utc = DateTime.fromMillis(epochMs, { zone: 'utc' });
  const central = utc.setZone('America/Chicago');
  return DateTime.local()
    .set({
      hour: central.hour,
      minute: central.minute,
      second: 0,
      millisecond: 0,
    })
    .toMillis();
}

export interface BuildScheduledEventFormConfigOptions {
  eventType: string;
  visitLocationId: number | null | undefined;
  addressOptions: Array<{ key: number; label: ReactNode; value: number }>;
  timeOptions: Array<{ key: number; label: ReactNode; value: number }>;
  minDate: DateTime;
  noteFieldRef: RefObject<NoteFieldRef | null>;
  noteMode: 'create' | 'edit';
  userId: string;
  scheduledEventId?: number;
  noteId?: number;
  draftId?: string;
  draftDiscardBaseline?: string;
  includeTrackingNames?: boolean;
  /** Propagates recovered-note state to the dialog header title. */
  onRecoveredNoteChange?: (isRecovered: boolean) => void;
  /** Edit-mode autosave via parent PATCH (`updateScheduledEvent` with `{ note }`). */
  noteSave?: NoteFieldSaveOverride;
}

/**
 * Builds the shared FormBuilder config for scheduled-event create/edit dialogs.
 * @param options - Dynamic field values and note-field wiring.
 * @returns FormBuilder config array.
 */
export function buildScheduledEventFormConfig({
  eventType,
  visitLocationId,
  addressOptions,
  timeOptions,
  minDate,
  noteFieldRef,
  noteMode,
  userId,
  scheduledEventId,
  noteId,
  draftId,
  draftDiscardBaseline,
  includeTrackingNames = false,
  onRecoveredNoteChange,
  noteSave,
}: BuildScheduledEventFormConfigOptions): FormConfig<ScheduledEventFormType> {
  const formConfig: FormConfig<ScheduledEventFormType> = [
    {
      type: 'label',
      label: 'Select an event type.',
    },
    {
      type: 'input',
      name: 'eventType',
      label: 'Event Type',
      input: 'radioGroup',
      props: {
        options: [
          { label: 'Phone call', value: 'call' },
          { label: 'Text message', value: 'message' },
          { label: 'Home visit', value: 'visit' },
        ],
        row: true,
        ...(includeTrackingNames
          ? { trackingName: 'event-type-radio-group' }
          : {}),
      },
    },
  ];

  if (eventType === 'visit') {
    formConfig.push({
      type: 'input',
      name: 'visitLocationId',
      label: 'Location',
      input: 'select',
      props: {
        options: addressOptions,
        fullWidth: true,
        ...(includeTrackingNames
          ? { trackingName: 'visit-location-select' }
          : {}),
      },
    });

    if (visitLocationId === -1) {
      formConfig.push({
        type: 'input',
        name: 'otherLocation',
        label: 'Location',
        input: 'text',
        props: {
          ...(includeTrackingNames
            ? { trackingName: 'other-location-input' }
            : {}),
        },
      });
    }
  }

  formConfig.push(
    {
      type: 'label',
      label: 'Select a time that works best.',
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'date',
          label: 'Date',
          input: 'date',
          props: {
            minDate,
            required: true,
            inputType: 'date',
            ...(includeTrackingNames
              ? { trackingName: 'event-date-input' }
              : {}),
          },
        },
        {
          type: 'input',
          name: 'time',
          label: 'Start time',
          input: 'select',
          props: {
            options: timeOptions,
            fullWidth: true,
            ...(includeTrackingNames
              ? { trackingName: 'event-time-select' }
              : {}),
          },
        },
      ],
    },
    {
      type: 'label',
      label: 'Leave a note to help you remember what to talk about.',
    },
    {
      type: 'input',
      name: 'note',
      label: 'Note',
      input: 'noteField',
      props: {
        ref: noteFieldRef,
        mode: noteMode,
        itemType: 'scheduled_event',
        itemId: scheduledEventId ? String(scheduledEventId) : undefined,
        noteId,
        userId,
        onRecoveredNoteChange,
        save: noteSave,
        draftType: 'events',
        draftId:
          draftId ??
          (noteMode === 'create' && !scheduledEventId
            ? MANUAL_SCHEDULED_EVENT_NOTE_DRAFT_ID
            : undefined),
        draftDiscardBaseline,
        ...(includeTrackingNames
          ? { trackingName: 'event-note-input' }
          : {}),
      },
    }
  );

  return formConfig;
}
